import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  DatePicker,
  Empty,
  Input,
  Popconfirm,
  Row,
  Col,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ClearOutlined,
  EyeOutlined,
  FilterOutlined,
  ReloadOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { caseAPI } from '../services/api';
import { useResponsive } from '../contexts/ResponsiveContext';
import MobileFilterSheet from '../components/mobile/MobileFilterSheet';
import MobileSectionCard from '../components/mobile/MobileSectionCard';
import { CALC_TYPE_LABELS, getDrugLabel } from '../constants/drugs';
import type { Case } from '../types';

const { Title, Text } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

const statusConfig: Record<string, { label: string; color: string }> = {
  archived: { label: '已归档', color: 'orange' },
  closed: { label: '已关闭', color: 'default' },
  active: { label: '进行中', color: 'green' },
};

const formatCaseTime = (value?: string) => (value ? new Date(value).toLocaleString('zh-CN') : '-');

const CaseHistory: React.FC = () => {
  const navigate = useNavigate();
  const { isMobileShell } = useResponsive();
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterDrug, setFilterDrug] = useState<string | undefined>();
  const [filterStatus, setFilterStatus] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (dateRange) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }
      params.page = String(page);
      params.page_size = String(pageSize);
      if (searchText) params.search = searchText;
      if (filterDrug) params.drug_name = filterDrug;
      if (filterStatus) params.status = filterStatus;
      const data = await caseAPI.history(params);
      if (Array.isArray(data)) { setCases(data); setTotal(data.length); }
      else { setCases(data.items); setTotal(data.total); }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadHistory(); }, [dateRange, page, pageSize, searchText, filterDrug, filterStatus]);

  const handleReactivate = async (caseId: string) => {
    try {
      await caseAPI.reactivate(caseId);
      message.success('病例已重新激活');
      loadHistory();
    } catch {
      message.error('重新激活失败');
    }
  };

  const drugOptions = useMemo(() => {
    const drugs = Array.from(new Set(cases.map((item) => item.drug_name).filter(Boolean)));
    return drugs.map((item) => ({ label: getDrugLabel(item), value: item }));
  }, [cases]);

  const filteredCases = cases;

  const hasActiveFilters = Boolean(filterDrug || filterStatus || dateRange);

  const clearAllFilters = () => {
    setSearchText('');
    setFilterDrug(undefined);
    setFilterStatus(undefined);
    setDateRange(null);
  };

  const columns = [
    {
      title: '患者编号',
      dataIndex: 'patient_code',
      key: 'patient_code',
      width: 160,
      render: (value: string | undefined, record: Case) => value || record.patient_id,
    },
    {
      title: '药物',
      dataIndex: 'drug_name',
      key: 'drug_name',
      width: 120,
      render: (value: string) => <Tag color="blue">{getDrugLabel(value)}</Tag>,
    },
    {
      title: '诊断摘要',
      dataIndex: 'diagnosis',
      key: 'diagnosis',
      render: (value: string | undefined) => value || '-',
    },
    {
      title: '最近计算',
      dataIndex: 'last_calculation_type',
      key: 'last_calculation_type',
      width: 130,
      render: (value: string | undefined) => (
        value ? <Tag color="purple">{CALC_TYPE_LABELS[value] || value}</Tag> : <Tag>暂无</Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (value: string) => {
        const config = statusConfig[value] || { color: 'default', label: value };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: formatCaseTime,
    },
    {
      title: '操作',
      key: 'actions',
      width: 170,
      render: (_: unknown, record: Case) => (
        <Space size={4}>
          <Button type="link" icon={<EyeOutlined />} onClick={() => navigate(`/cases/${record.id}`)}>
            查看
          </Button>
          <Popconfirm
            title="确认重新激活此病例？"
            onConfirm={() => handleReactivate(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Button type="link" icon={<UndoOutlined />}>
              激活
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filterFields = (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Select
        placeholder="按药物筛选"
        allowClear
        value={filterDrug}
        onChange={setFilterDrug}
        options={drugOptions}
      />
      <Select
        placeholder="按状态筛选"
        allowClear
        value={filterStatus}
        onChange={setFilterStatus}
        options={[
          { label: '已归档', value: 'archived' },
          { label: '已关闭', value: 'closed' },
        ]}
      />
      <RangePicker
        style={{ width: '100%' }}
        value={dateRange}
        onChange={(value) => setDateRange(value as [dayjs.Dayjs, dayjs.Dayjs] | null)}
      />
    </Space>
  );

  const renderMobileCards = () => {
    if (!filteredCases.length) {
      return <Empty description="暂无历史病例" style={{ padding: '48px 0' }} />;
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {filteredCases.map((item) => {
          const config = statusConfig[item.status] || { color: 'default', label: item.status };
          return (
            <MobileSectionCard
              key={item.id}
              title={item.patient_code || item.patient_id}
              extra={<Tag color={config.color}>{config.label}</Tag>}
            >
              <Space wrap size={[8, 8]} style={{ marginBottom: 10 }}>
                <Tag color="blue">{getDrugLabel(item.drug_name)}</Tag>
                {item.last_calculation_type ? (
                  <Tag color="purple">{CALC_TYPE_LABELS[item.last_calculation_type] || item.last_calculation_type}</Tag>
                ) : null}
              </Space>

              <div style={{ marginBottom: 12 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>诊断摘要</Text>
                <Text>{item.diagnosis || '暂无诊断信息'}</Text>
              </div>

              <div style={{ marginBottom: 14 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>归档/更新时间</Text>
                <Text>{formatCaseTime(item.updated_at)}</Text>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <Button
                  type="primary"
                  icon={<EyeOutlined />}
                  block
                  onClick={() => navigate(`/cases/${item.id}`)}
                >
                  查看详情
                </Button>
                <Popconfirm
                  title="确认重新激活此病例？"
                  onConfirm={() => handleReactivate(item.id)}
                  okText="确认"
                  cancelText="取消"
                >
                  <Button icon={<UndoOutlined />} block>
                    重新激活
                  </Button>
                </Popconfirm>
              </div>
            </MobileSectionCard>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobileShell ? 'flex-start' : 'center',
          flexDirection: isMobileShell ? 'column' : 'row',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div>
          <Title level={4} style={{ margin: 0 }}>病例历史</Title>
          <Text type="secondary">按时间、状态和药物查看归档病例，移动端优先展示关键信息。</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={loadHistory} loading={loading}>
          刷新
        </Button>
      </div>

      {isMobileShell ? (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <Search
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              onSearch={setSearchText}
              placeholder="搜索患者编号或诊断"
              allowClear
              style={{ flex: 1 }}
            />
            <Button icon={<FilterOutlined />} onClick={() => setFilterOpen(true)}>
              筛选
            </Button>
          </div>

          {hasActiveFilters ? (
            <div style={{ marginBottom: 12 }}>
              <Button type="link" icon={<ClearOutlined />} onClick={clearAllFilters} style={{ paddingInline: 0 }}>
                清空筛选
              </Button>
            </div>
          ) : null}

          {renderMobileCards()}

          <MobileFilterSheet
            open={filterOpen}
            title="筛选历史病例"
            onClose={() => setFilterOpen(false)}
            onReset={clearAllFilters}
            onApply={() => setFilterOpen(false)}
          >
            {filterFields}
          </MobileFilterSheet>
        </>
      ) : (
        <>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Row gutter={[12, 12]} align="middle">
              <Col flex="1 1 240px">
                <Search
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  onSearch={setSearchText}
                  placeholder="搜索患者编号或诊断"
                  allowClear
                />
              </Col>
              <Col flex="0 1 160px">
                <Select
                  style={{ width: '100%' }}
                  placeholder="按药物筛选"
                  allowClear
                  value={filterDrug}
                  onChange={setFilterDrug}
                  options={drugOptions}
                />
              </Col>
              <Col flex="0 1 140px">
                <Select
                  style={{ width: '100%' }}
                  placeholder="按状态筛选"
                  allowClear
                  value={filterStatus}
                  onChange={setFilterStatus}
                  options={[
                    { label: '已归档', value: 'archived' },
                    { label: '已关闭', value: 'closed' },
                  ]}
                />
              </Col>
              <Col flex="0 1 260px">
                <RangePicker
                  style={{ width: '100%' }}
                  value={dateRange}
                  onChange={(value) => setDateRange(value as [dayjs.Dayjs, dayjs.Dayjs] | null)}
                />
              </Col>
              {hasActiveFilters ? (
                <Col>
                  <Button type="link" icon={<ClearOutlined />} onClick={clearAllFilters}>
                    清空筛选
                  </Button>
                </Col>
              ) : null}
            </Row>
          </Card>

          <Card>
            <Table
              rowKey="id"
              columns={columns}
              dataSource={filteredCases}
              loading={loading}
              scroll={{ x: 1040 }}
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: true,
                pageSizeOptions: ['20', '50', '100'],
                onChange: (nextPage, nextSize) => {
                  if (nextSize !== pageSize) { setPageSize(nextSize); setPage(1); }
                  else setPage(nextPage);
                },
                showTotal: (total) => `共 ${total} 条历史记录`,
              }}
              locale={{ emptyText: '暂无历史病例' }}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default CaseHistory;
